/**
 * Card 2508 — a board's DECLARED columns (`status_view.list_fields`), in words.
 *
 * <p>The engine sends each row's declared columns as `fields: { <name>: { type, value } }`, in
 * declaration order. This file is the one place the portal turns those into cells: a `date` as a
 * day ("12 Sep 2026", marked when it has passed or is close), a `badge` as a pill, a `text` as
 * itself. The values are the TENANT's (a client code, a band, a date); the portal never switches on
 * a field's name, with one declared convention below.</p>
 *
 * <h3>Placeholder dates (D5)</h3>
 * <p>A date column `<name>` may be accompanied by a text column `<name>_placeholder` whose value is
 * true while the date is a stand-in (an imported record whose real date is not decided yet). The
 * page then does not show the stand-in as if it were real: the cell reads the flag column's own
 * declared label ("Review date to be set") with the stand-in date beside it in brackets, and the flag
 * column itself is never drawn. Nothing here names the tenant's field: the convention is the
 * suffix, and the words are the declaration's.</p>
 */
import { Pill, type PillTone } from "@ardept/design-system";
import type { JobStatusFieldType, JobStatusFieldValue, JobStatusFile } from "../api/types.js";

/** One row's (or one record's) declared values, keyed by field name. */
export type FieldValues = Readonly<Record<string, JobStatusFieldValue>> | undefined;

/** The suffix that marks a column as another date column's placeholder flag. */
export const PLACEHOLDER_SUFFIX = "_placeholder";

/** What a placeholder date reads when its flag column declares no label of its own. */
export const PLACEHOLDER_FALLBACK = "Date to be set";

/** What an empty cell reads. */
export const NOT_RECORDED = "Not recorded";

/** A date this many days away or closer is marked as close. */
const SOON_DAYS = 30;

const DAY_MS = 86_400_000;

/** One column a board draws. */
export interface BoardColumn {
  name: string;
  label: string;
  type: JobStatusFieldType;
  /** The name of the column that says this date is a placeholder, or null. */
  placeholderFlag: string | null;
}

/** "review_due" → "Review due": a field's name in words, for a column the engine sent no label for. */
export function humanise(name: string): string {
  const words = name.replace(/[_-]+/g, " ").trim();
  return words.length === 0 ? name : words[0]!.toUpperCase() + words.slice(1).toLowerCase();
}

/** A flag column's value, read as yes or no. Text on the wire, so "true" in any case is yes. */
export function isYes(value: string | null | undefined): boolean {
  if (value === null || value === undefined) return false;
  return ["true", "yes", "1"].includes(value.trim().toLowerCase());
}

/**
 * The columns to draw, in the order the engine sent them (declaration order), across every row so a
 * column empty on the first row still appears. A `<name>_placeholder` flag whose date column exists
 * is folded into that column rather than drawn.
 */
export function columnsFor(files: readonly Pick<JobStatusFile, "fields">[]): BoardColumn[] {
  const seen = new Map<string, JobStatusFieldValue>();
  for (const file of files) {
    for (const [name, field] of Object.entries(file.fields ?? {})) {
      if (!seen.has(name) || (seen.get(name)?.label === undefined && field.label !== undefined)) {
        seen.set(name, field);
      }
    }
  }
  const names = [...seen.keys()];
  const flags = new Set(
    names.filter(
      (name) => name.endsWith(PLACEHOLDER_SUFFIX) && seen.has(name.slice(0, -PLACEHOLDER_SUFFIX.length)),
    ),
  );
  return names
    .filter((name) => !flags.has(name))
    .map((name) => {
      const field = seen.get(name)!;
      const flag = `${name}${PLACEHOLDER_SUFFIX}`;
      return {
        name,
        label: labelOf(name, field),
        type: field.type,
        placeholderFlag: flags.has(flag) ? flag : null,
      };
    });
}

/** A field's declared label, or its name in words. */
export function labelOf(name: string, field: JobStatusFieldValue | undefined): string {
  const label = field?.label;
  return typeof label === "string" && label.trim() !== "" ? label : humanise(name);
}

/** "12 Sep 2026". `null` for a value that is not a date. */
export function fullDay(iso: string | null | undefined): string | null {
  if (iso === null || iso === undefined || iso.trim() === "") return null;
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return null;
  return when.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

/** How a date stands against today: passed, close, or neither. */
export function dateStanding(iso: string, now: number): "passed" | "soon" | "later" | null {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return null;
  const day = Math.floor(when.getTime() / DAY_MS);
  const today = Math.floor(now / DAY_MS);
  if (day < today) return "passed";
  if (day - today <= SOON_DAYS) return "soon";
  return "later";
}

/** The pill tone for a band-like badge: high reads as a problem, medium as attention, low as fine. */
export function badgeTone(value: string): PillTone {
  switch (value.trim().toLowerCase()) {
    case "high":
      return "bad";
    case "medium":
      return "attention";
    case "low":
      return "good";
    default:
      return "neutral";
  }
}

/** "medium" → "Medium": a badge's value in sentence case. */
function sentenceCase(value: string): string {
  const text = value.trim();
  return text.length === 0 ? text : text[0]!.toUpperCase() + text.slice(1);
}

/** Is this row's date column a placeholder right now? */
export function isPlaceholder(fields: FieldValues, column: BoardColumn): boolean {
  return column.placeholderFlag !== null && isYes(fields?.[column.placeholderFlag]?.value);
}

/** The words a placeholder date reads: the flag column's declared label, else the fallback. */
export function placeholderText(fields: FieldValues, column: BoardColumn): string {
  const flag = column.placeholderFlag === null ? undefined : fields?.[column.placeholderFlag];
  const label = flag?.label;
  return typeof label === "string" && label.trim() !== "" ? label : PLACEHOLDER_FALLBACK;
}

/** One cell: the column's value on this row, in words. */
export function FieldCell({
  fields,
  column,
  now,
}: {
  fields: FieldValues;
  column: BoardColumn;
  now: number;
}): JSX.Element {
  const field = fields?.[column.name];
  const value = field?.value ?? null;
  if (value === null || value.trim() === "") return <span className="vb-none">{NOT_RECORDED}</span>;

  const type = field?.type ?? column.type;
  if (type === "date") {
    const day = fullDay(value);
    if (day === null) return <span>{value}</span>;
    if (isPlaceholder(fields, column)) {
      return (
        <span className="vb-placeholder" data-placeholder="true">
          {placeholderText(fields, column)} <span className="vb-placeholder__day">(placeholder {day})</span>
        </span>
      );
    }
    const standing = dateStanding(value, now);
    if (standing === "passed") {
      return (
        <Pill tone="bad" fullLabel={`${day}, already passed`}>
          {day}
        </Pill>
      );
    }
    if (standing === "soon") {
      return (
        <Pill tone="attention" fullLabel={`${day}, coming up`}>
          {day}
        </Pill>
      );
    }
    return <span className="vb-day">{day}</span>;
  }
  if (type === "badge") return <Pill tone={badgeTone(value)}>{sentenceCase(value)}</Pill>;
  return <span>{value}</span>;
}
